import axiosInstance from "./axiosInstance";

// Set auth token in axios headers
export const setAuthToken = (token) => {
  if (token) {
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common['Authorization'];
  }
};

// Login user
export const login = async (email, password) => {
  try {
    const response = await axiosInstance.post(`/shared/login`, { email, password });
    if (response.data.token) {
      // Store token
      localStorage.setItem('token', response.data.token);
      setAuthToken(response.data.token);
      
      // Store user ID if available
      if (response.data.user && response.data.user.id) {
        localStorage.setItem('userId', response.data.user.id.toString());
      }
      
      // Store complete user info object
      localStorage.setItem('userInfo', JSON.stringify({
        id: response.data.user?.id,
        email: response.data.user?.email,
        name: response.data.user?.name,
        role: response.data.user?.role,
      }));
      
      // Store user role
      if (response.data.user && response.data.user.role) {
        localStorage.setItem('userRole', response.data.user.role);
      } else if (response.data.role) {
        localStorage.setItem('userRole', response.data.role);
      }
      
      // Store user type if available
      if (response.data.userType) {
        localStorage.setItem('userType', response.data.userType);
      }
    }
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};

// Logout user
export const logout = (nav) => {
  // Remove all authentication-related keys from localStorage
  localStorage.removeItem('token');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userId');
  localStorage.removeItem('userInfo');
  localStorage.removeItem('user');
  localStorage.removeItem('userType');
  localStorage.removeItem('reg_email');
  localStorage.removeItem('reg_password');
  
  setAuthToken(null);
  
  // Redirect to Form page if navigate function is provided
  if (nav) {
    nav('/');
  }
};

// Get current user
export const getCurrentUser = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    
    setAuthToken(token);
    const response = await axiosInstance.get(`/auth/me`);
    return response.data;
  } catch (error) {
    logout();
    return null;
  }
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};

// Get user ID consistently
export const getUserId = () => {
  return localStorage.getItem('userId');
};

// Get user info consistently
export const getUserInfo = () => {
  const userInfo = localStorage.getItem('userInfo');
  return userInfo ? JSON.parse(userInfo) : null;
};

// Get user role consistently
export const getUserRole = () => {
  return localStorage.getItem('userRole');
};

// Get authentication token consistently
export const getToken = () => {
  return localStorage.getItem('token');
};

// Get user ID from JWT token (more reliable than localStorage)
export const getUserIdFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.userId || payload.id || payload.sub;
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return null;
  }
};

// Enhanced authentication context provider
export const createAuthContext = () => {
  const token = getToken();
  const userRole = localStorage.getItem('userRole');
  const userId = getUserIdFromToken() || localStorage.getItem('userId');
  const userInfo = getUserInfo();
  
  return {
    token,
    userRole,
    userId,
    userInfo,
    isAuthenticated: !!token
  };
};

// Validate and refresh authentication state
export const validateAuthState = () => {
  const token = getToken();
  const userRole = localStorage.getItem('userRole');
  const userId = getUserIdFromToken();
  
  if (!token) {
    console.warn('No authentication token found');
    return false;
  }
  
  if (!userRole) {
    console.warn('No user role found in localStorage');
    return false;
  }
  
  if (!userId) {
    console.warn('No user ID found in token or localStorage');
    return false;
  }
  
  return true;
};

// Ensure authentication data is consistent across storage
export const syncAuthData = () => {
  const token = getToken();
  if (!token) return;
  
  // Extract user ID from token if not in localStorage
  const tokenUserId = getUserIdFromToken();
  if (tokenUserId && !localStorage.getItem('userId')) {
    localStorage.setItem('userId', tokenUserId.toString());
    console.log('Synced user ID from token to localStorage:', tokenUserId);
  }
  
  // Ensure token is set in axios defaults
  if (typeof window !== 'undefined' && window.axios) {
    window.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
};
